// The box "Ask the agent" opens on a Notebook cell: one question about that
// cell, put in the message box for the person to send. Props and callbacks
// only; the cell owns whether it is open.
import React, { useState } from 'react'
import { Box, Button, TextField } from '@mui/material'
import { HAIRLINE } from '../../../agent/components/surface'
import DisabledReason from '../../system/DisabledReason'

export interface NotebookAskBoxProps {
  cellTitle: string
  /** Puts the question in the message box; false when it could not. */
  onAsk: (question: string) => boolean
  onCancel: () => void
}

export const NotebookAskBox: React.FC<NotebookAskBoxProps> = ({ cellTitle, onAsk, onCancel }) => {
  const [question, setQuestion] = useState('')
  const blank = question.trim() === ''

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!blank && onAsk(question)) setQuestion('')
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    // Escape closes this box, not the Notebook around it.
    if (e.key !== 'Escape') return
    e.stopPropagation()
    onCancel()
  }

  return (
    <Box
      component="form"
      onSubmit={handleSubmit}
      data-testid="notebook-ask-box"
      sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, px: 1.5, py: 1, borderBottom: HAIRLINE, flexWrap: 'wrap' }}
    >
      <TextField
        autoFocus
        size="small"
        fullWidth
        value={question}
        onChange={(e) => setQuestion(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder="What would you like to change or know about this?"
        slotProps={{ htmlInput: { 'aria-label': `Ask the agent about “${cellTitle}”`, 'data-testid': 'notebook-ask-input' } }}
        sx={{ flex: '1 1 16rem' }}
      />
      <Box sx={{ display: 'flex', gap: 1 }}>
        <DisabledReason reason={blank ? 'Type a question first.' : undefined}>
          <Button type="submit" size="small" variant="contained" color="info" data-testid="notebook-ask-submit">
            Ask
          </Button>
        </DisabledReason>
        <Button size="small" color="info" onClick={onCancel} data-testid="notebook-ask-cancel">
          Cancel
        </Button>
      </Box>
    </Box>
  )
}

export default NotebookAskBox
